// Validates a `redirectTo` value read from the URL before it is used as a
// navigation target. Only relative, in-app paths are allowed — this prevents
// open redirects from links like /login?redirectTo=//evil.com.
//
// Mirrors the server-side guard in packages/server/src/apps/saml/samlStrategy.ts
// so the SAML round-trip and the client-side login sinks enforce the same rule.
export const sanitizeRedirectTo = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return undefined;
  }
  // Reject a second slash/backslash right after the leading one (//evil.com,
  // /\evil.com — browsers resolve these off-origin) and any backslash or
  // control character anywhere, which can smuggle an authority component.
  // eslint-disable-next-line no-control-regex
  if (/^\/[/\\]/.test(value) || /[ -\\]/.test(value)) {
    return undefined;
  }
  // Reject a value that smuggles its own nested redirectTo (e.g.
  // /login?redirectTo=//evil.com), which a downstream sink would then follow.
  const queryString = value.includes('?')
    ? value.slice(value.indexOf('?'))
    : '';
  if (/[?&]redirectTo=/i.test(queryString)) {
    return undefined;
  }
  return value;
};

// Returns a safe navigation target: the validated redirectTo, or '/' when it is
// missing or unsafe.
export const safeRedirectTo = (value: unknown): string => {
  return sanitizeRedirectTo(value) ?? '/';
};
